"use client";

import type { CSSProperties, ReactNode } from "react";
import {
	ArtworkThumbnail,
	GlassCard,
	type ListRowProps,
	MediaRow,
} from "@/components/design-system/ArtistDesignSystem";
import type { DiscoveryTrack } from "@/domain/discovery/DiscoveryRepository";
import { playbackHref } from "@/domain/discovery/DiscoveryRepository";

const cardStyle: CSSProperties = { padding: 0 };
const dividerStyle: CSSProperties = {
	height: 1,
	background: "var(--ds-divider)",
	marginInline: "var(--ds-space-lg)",
};

function formatDuration(seconds: number): string {
	if (seconds <= 0) return "—";
	const minutes = Math.floor(seconds / 60);
	const secondsPart = Math.floor(seconds % 60);
	return `${minutes}:${String(secondsPart).padStart(2, "0")}`;
}

/**
 * SearchResultsList — results list matching iOS `DSGlassCard(padding: 0)` +
 * `VStack` of `DSMediaRow` with divider strokes. Each row shows the track
 * artwork (50×50), title, artist subtitle, and formatted duration trailing.
 * Tapping a row routes to the player with the full result queue.
 */
export function SearchResultsList({
	tracks,
	onUnavailable,
}: Readonly<{
	tracks: readonly DiscoveryTrack[];
	onUnavailable?: () => void;
}>) {
	if (tracks.length === 0) return null;
	return (
		<div className="search-results-list" aria-live="polite">
			<GlassCard style={cardStyle} className="search-results-list__card">
				{tracks.map((track, index) => {
					const rowProps = buildRowProps(track, tracks, onUnavailable);
					return (
						<div key={track.id} className="search-results-list__row-wrapper">
							<MediaRow {...rowProps} />
							{index < tracks.length - 1 ? (
								<div style={dividerStyle} aria-hidden="true" />
							) : null}
						</div>
					);
				})}
			</GlassCard>
		</div>
	);
}

function buildRowProps(
	track: DiscoveryTrack,
	queue: readonly DiscoveryTrack[],
	onUnavailable: (() => void) | undefined,
): ListRowProps {
	const href = playbackHref(track, queue);
	const leading: ReactNode = (
		<ArtworkThumbnail
			{...(track.artworkURL === undefined ? {} : { src: track.artworkURL })}
			size={50}
			alt={`${track.title} 封面`}
		/>
	);
	const trailing: ReactNode = (
		<span style={{ color: "var(--ds-text-secondary)", fontSize: "0.86rem" }}>
			{formatDuration(track.durationSeconds)}
		</span>
	);
	const baseProps: ListRowProps = {
		title: track.title,
		subtitle: track.artist,
		leading,
		trailing,
	};
	if (href !== null) return { ...baseProps, href };
	if (onUnavailable !== undefined)
		return { ...baseProps, onClick: onUnavailable };
	return baseProps;
}
